import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import axiosInstance from '../../services/axiosMiddleware';
import { setAddressSuggestionList, setAgentInfo, setError, setLoading, setTemplates } from './formTemplateSlice';
import { AddressSuggestionResponse, FetchFormByPropertyResponse, FetchTenantFormTemplatesResponse } from './formTemplateTypes';
import { setAppRegion } from '../../utils/petFormHelpers';

export const fetchFormTemplates = createAsyncThunk(
    'tenant/fetchFormTemplates',
    async ({ agentId, region }: { agentId: string; region?: string }, { dispatch }) => {
        dispatch(setLoading(true));
        try {
            const params = region ? { region } : {};
            const response = await axiosInstance.get(`/forms/agent_application/${agentId}`, { params });
            dispatch(setTemplates(
                (response.data.data.form_data || []).map((section: any) => ({
                    ...section,
                    fields: section.fields || []
                }))
            ));
            dispatch(setError(null));
        } catch (error: any) {
            dispatch(setError(error.message || 'Failed to fetch form templates'));
        } finally {
            dispatch(setLoading(false));
        }
    }
);

// export const fetchTenantFormTemplates = createAsyncThunk(
//     'tenant/fetchTenantFormTemplates',
//     async ({ agentId, formId }: { agentId: string; formId: string }, { dispatch, rejectWithValue }) => {
//         try {
//             const response: AxiosResponse<FetchTenantFormTemplatesResponse> = await axiosInstance.get(`/forms/tenant_application/${agentId}/${formId}`);
//             // dispatch(setTemplates(response.data.data.form_data));
//             dispatch(setTemplates(
//                 (response.data.data.form_data || []).map((section: any) => ({
//                     ...section,
//                     fields: section.fields || []
//                 }))
//             ));
//             dispatch(setError(null));
//             return response.data;
//         } catch (error: unknown) {
//             if (axios.isAxiosError(error)) {
//                 return rejectWithValue(error.response?.data || error.message);
//             }
//             return rejectWithValue('An unexpected error occurred');
//         }
//     }
// );

export const fetchTenantFormTemplates = createAsyncThunk(
    'tenant/fetchTenantFormTemplates',
    async ({ agentId, region }: { agentId: string; region?: string }, { dispatch, rejectWithValue }) => {
        try {
            const params = region ? { region } : {};
            const response: AxiosResponse<FetchTenantFormTemplatesResponse> = await axiosInstance.get(`/forms/tenant_application/${agentId}`, { params });
            // dispatch(setTemplates(response.data.data.form_data));
            dispatch(setTemplates(
                (response.data.data.form_data || []).map((section: any) => ({
                    ...section,
                    fields: section.fields || []
                }))
            ));
            dispatch(setError(null));

            // Region is now determined by URL path (/pet-request/ vs /pet-request-qld/)
            // and set in the Tenant component's useEffect

            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const fetchFormByProperty = createAsyncThunk(
    'tenant/fetchFormByProperty',
    async ({ propertyId }: { propertyId: string; }, { dispatch, rejectWithValue }) => {
        try {
            const response: AxiosResponse<FetchFormByPropertyResponse> = await axiosInstance.get(`/property/${propertyId}/form`);
            // Resumed applications (emailed continue link) never pass through the
            // pet-request route, so nothing else establishes the region for them.
            // Take it from the property this application belongs to — the same
            // source as the form body, so the two cannot disagree. Must run before
            // setTemplates: that dispatch is what re-renders the header that reads it.
            setAppRegion(response.data.data?.form?.region);
            await dispatch(setTemplates(
                (response.data.data.form?.form_data || []).map((section: any) => ({
                    ...section,
                    fields: section.fields || []
                }))
            ));
            await dispatch(setAgentInfo(response.data.data?.agent || {}));
            await dispatch(setError(null));

            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);


export const uploadDocument = createAsyncThunk(
  "tenant/uploadDocument",
  async (
    { formData }: { formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/tenant/documents/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || error.message);
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const deleteDocument = createAsyncThunk(
  "tenant/deleteDocument",
  async (
    { documentId }: { documentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.delete(
        `/tenant/documents/${documentId}`
      );
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || error.message);
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const fetchAddress = createAsyncThunk(
    'tenant/fetchAddress',
    async ({ searchKey }: { searchKey: string; }, { dispatch, rejectWithValue }) => {
        try {
            const response: AxiosResponse<AddressSuggestionResponse> = await axiosInstance.get(`/addresses-list?address=${searchKey}`);
            await dispatch(setAddressSuggestionList(response?.data?.data || []));
            return response.data;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                return rejectWithValue(error.response?.data || error.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);